import { db } from '../firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  deleteDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

const CODES_COLLECTION = 'authCodes';
const CODE_EXPIRY_MINUTES = 15; // Codes expire after 15 minutes
const CODE_LENGTH = 6;

/**
 * Generate a secure random 6-digit code
 */
function generateCode() {
  // Use crypto.getRandomValues for secure random generation
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  // Generate a 6-digit code (100000 to 999999)
  const code = 100000 + (array[0] % 900000);
  return code.toString();
}

/**
 * Normalize email address (lowercase, trim)
 */
function normalizeEmail(email) {
  return email.toLowerCase().trim();
}

/**
 * Generate and store an authentication code for an email
 * @param {string} email - User's email address
 * @param {object} options - Additional options (isRSVP, eventId, etc.)
 * @returns {Promise<{success: boolean, code?: string, error?: string}>}
 */
export async function generateAuthCode(email, options = {}) {
  try {
    const normalizedEmail = normalizeEmail(email);
    
    // Check for rate limiting - prevent too many code requests
    // Query without expiresAt filter to avoid needing composite index, filter in memory instead
    const existingCodesQuery = query(
      collection(db, CODES_COLLECTION),
      where('email', '==', normalizedEmail)
    );
    const existingCodesSnapshot = await getDocs(existingCodesQuery);
    
    // Filter out expired codes in memory
    const now = Timestamp.now();
    const activeCodes = existingCodesSnapshot.docs.filter(doc => {
      const codeData = doc.data();
      return codeData.expiresAt && codeData.expiresAt.toMillis() > now.toMillis() && !codeData.used;
    });
    
    // Rate limit: max 3 active codes per email
    if (activeCodes.length >= 3) {
      return { 
        success: false, 
        error: 'Too many active codes. Please wait a few minutes or check your email for an existing code.' 
      };
    }
    
    // Generate new code
    const code = generateCode();
    const expiresAt = Timestamp.now().toMillis() + (CODE_EXPIRY_MINUTES * 60 * 1000);
    
    // Store code in Firestore
    const codeDoc = {
      email: normalizedEmail,
      code: code,
      expiresAt: Timestamp.fromMillis(expiresAt),
      createdAt: serverTimestamp(),
      used: false,
      ...options // Include any additional options (isRSVP, eventId, etc.)
    };
    
    const codeRef = doc(collection(db, CODES_COLLECTION));
    await setDoc(codeRef, codeDoc);
    
    // Clean up expired codes for this email (async, don't wait)
    cleanupExpiredCodes(normalizedEmail).catch(console.error);
    
    return { success: true, code };
  } catch (error) {
    console.error('Error generating auth code:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Verify an authentication code
 * @param {string} email - User's email address
 * @param {string} code - The 6-digit code to verify
 * @returns {Promise<{success: boolean, valid: boolean, error?: string, data?: object}>}
 */
export async function verifyAuthCode(email, code) {
  try {
    const normalizedEmail = normalizeEmail(email);
    const normalizedCode = code.trim();
    
    if (!normalizedCode || normalizedCode.length !== CODE_LENGTH || !/^\d+$/.test(normalizedCode)) {
      return { success: true, valid: false, error: 'Invalid code format' };
    }
    
    // Find the code document
    // Query by email and code only (avoiding used filter to prevent index requirement)
    const codesQuery = query(
      collection(db, CODES_COLLECTION),
      where('email', '==', normalizedEmail),
      where('code', '==', normalizedCode)
    );
    
    const codesSnapshot = await getDocs(codesQuery);
    
    if (codesSnapshot.empty) {
      return { success: true, valid: false, error: 'Invalid or expired code' };
    }
    
    // Get the first matching code (should only be one)
    const codeDoc = codesSnapshot.docs[0];
    const codeData = codeDoc.data();
    
    // Check if code has been used
    if (codeData.used) {
      return { success: true, valid: false, error: 'Code has already been used' };
    }
    
    // Check if code has expired
    const now = Timestamp.now();
    if (codeData.expiresAt.toMillis() < now.toMillis()) {
      // Mark as used and delete
      await deleteDoc(codeDoc.ref);
      return { success: true, valid: false, error: 'Code has expired' };
    }
    
    // Mark code as used
    await setDoc(codeDoc.ref, { used: true }, { merge: true });
    
    // Return success with any additional data stored with the code
    // Exclude internal fields, keep only custom data (isRSVP, eventId, etc.)
    const { email: _, code: __, used: ___, createdAt: ____, expiresAt: _____, ...additionalData } = codeData;
    
    return { 
      success: true, 
      valid: true, 
      data: additionalData 
    };
  } catch (error) {
    console.error('Error verifying auth code:', error);
    return { success: false, valid: false, error: error.message };
  }
}

/**
 * Clean up expired codes for an email (helper function)
 */
async function cleanupExpiredCodes(email) {
  try {
    const normalizedEmail = normalizeEmail(email);
    // Query without expiresAt filter to avoid needing composite index, filter in memory instead
    const codesQuery = query(
      collection(db, CODES_COLLECTION),
      where('email', '==', normalizedEmail)
    );
    
    const codesSnapshot = await getDocs(codesQuery);
    const now = Timestamp.now();
    
    // Filter expired codes in memory and delete them
    const expiredCodes = codesSnapshot.docs.filter(doc => {
      const codeData = doc.data();
      return codeData.expiresAt && codeData.expiresAt.toMillis() < now.toMillis();
    });
    
    const deletePromises = expiredCodes.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
  } catch (error) {
    console.error('Error cleaning up expired codes:', error);
  }
}

/**
 * Invalidate all codes for an email (useful after successful login)
 */
export async function invalidateAllCodesForEmail(email) {
  try {
    const normalizedEmail = normalizeEmail(email);
    const codesQuery = query(
      collection(db, CODES_COLLECTION),
      where('email', '==', normalizedEmail)
    );
    
    const codesSnapshot = await getDocs(codesQuery);
    const deletePromises = codesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    return { success: true };
  } catch (error) {
    console.error('Error invalidating codes:', error);
    return { success: false, error: error.message };
  }
}
